import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

const BOOKING_URL =
  "https://calendar.google.com/calendar/appointments/schedules/AcZssZ37WN8im6rHWGYl7-t1jKCp0KwNkROERRgi38q9IyI9IKZKgK1aNv_EEDUmxhhsNK3WS2Pnz35F?gv=true";

type BookingContextValue = {
  openBooking: () => void;
};

const BookingContext = createContext<BookingContextValue>({ openBooking: () => undefined });

export function useBooking() {
  return useContext(BookingContext);
}

export function BookingProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const openBooking = useCallback(() => setIsOpen(true), []);
  const closeBooking = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeBooking();
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, closeBooking]);

  return (
    <BookingContext.Provider value={{ openBooking }}>
      {children}
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          role="dialog"
          aria-modal="true"
          aria-label="Agenda tu cita"
        >
          <div
            className="absolute inset-0 bg-foreground-950/70 backdrop-blur-sm animate-fade-in"
            onClick={closeBooking}
          ></div>

          <div className="relative z-10 w-full max-w-3xl bg-background-50 rounded-lg border border-background-200/80 overflow-hidden flex flex-col max-h-[90vh] animate-fade-up">
            <div className="flex items-center justify-between gap-4 px-5 md:px-7 py-4 md:py-5 border-b border-background-200/80">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 flex-shrink-0">
                  <i className="ri-calendar-check-line text-xl"></i>
                </span>
                <div>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
                    Agenda tu cita
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600">
                    Elige el día y la hora que mejor te acomode
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeBooking}
                aria-label="Cerrar"
                className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-auto bg-background-50">
              <iframe
                src={BOOKING_URL}
                title="Agenda de reservas de Laboratorio Clínico ANEDAN"
                style={{ border: 0 }}
                width="100%"
                height={600}
                frameBorder={0}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </BookingContext.Provider>
  );
}